import { Text } from '@podiumhq/design-system';

import { useSelectPlanCheckoutContext } from 'contexts/SelectPlanCheckoutContextProvider';
import env from 'env';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';

import FullScreenSpinner from '../LoadingElements/FullScreenSpinner';
import Notification from '../Notification';

import {
  SelectPlanCheckoutContentBox,
  SelectPlanCheckoutContentFlexWrapper,
  SelectPlanCheckoutDescription,
  SelectPlanCheckoutTitle,
} from './components/layout';
import {
  BillingDetailsSummary,
  ContactDetailsSummary,
  PaymentSummary,
  SelectPlanTermSummary,
} from './components/PlanSummary';
import SelectPlanPurchase from './components/SelectPlanPurchase';
import SelectedPlanSummary from './components/SelectedPlanSummary';
import { CONFIRM_VIEW_ROUTE, SUCCESSFUL_PAID_CONVERSION } from './constants';
import { useConfirmPurchase } from './hooks';

const PlanSummaryView = () => {
  const confirmPurchase = useConfirmPurchase();
  const {
    changeCheckoutPage,
    isMobile,
    paymentRequestLoading,
    selectedPlan = {},
    selectedTerm,
    setHasCompletedPurchase,
    setPaymentRequestLoading,
  } = useSelectPlanCheckoutContext() || {};
  const trackEvent = useTrackAmplitudeEvent(
    'selectPlanCheckout.confirmDetails.visited'
  );

  const handlePurchaseResponse = (status) => {
    trackEvent(`selectPlanCheckout.confirmDetails.${status}`);
    setHasCompletedPurchase(true);
    setPaymentRequestLoading(false);
  };

  const handleConfirmPurchase = () => {
    trackEvent('selectPlanCheckout.confirmDetails.completePurchaseClicked');

    confirmPurchase()
      .then((response) => {
        if (!!response) {
          handlePurchaseResponse('success');

          const isInIframe = window !== window?.parent;
          if (isInIframe) {
            window?.parent?.postMessage(
              SUCCESSFUL_PAID_CONVERSION,
              env.KAZAAM_URL
            );
          }
          return changeCheckoutPage(CONFIRM_VIEW_ROUTE);
        }

        handlePurchaseResponse('error');
      })
      .catch((error) => {
        handlePurchaseResponse('error');
      });
  };

  const selectPlanPurchaseProps = {
    disabled: paymentRequestLoading,
    isLoading: paymentRequestLoading,
    price: selectedPlan?.monthlyPrice,
    submitPurchase: handleConfirmPurchase,
  };

  return (
    <>
      <SelectPlanCheckoutTitle>
        Ready to grow with Podium?
      </SelectPlanCheckoutTitle>
      <SelectPlanCheckoutDescription>
        Confirm your details and start your plan.
      </SelectPlanCheckoutDescription>

      {isMobile && (
        <>
          <SelectedPlanSummary maxWidth="100%" mb="12px" mt="24px" />
          <SelectPlanPurchase
            displayDisclaimer={false}
            {...selectPlanPurchaseProps}
          />
        </>
      )}

      <SelectPlanCheckoutContentFlexWrapper mt={isMobile ? '24px' : '40px'}>
        <SelectPlanCheckoutContentBox>
          <ContactDetailsSummary />

          <SelectPlanTermSummary />

          <PaymentSummary />

          <BillingDetailsSummary />

          <Notification shouldScrollToError />

          {!isMobile && (
            <SelectPlanPurchase
              term={selectedTerm}
              {...selectPlanPurchaseProps}
            />
          )}
        </SelectPlanCheckoutContentBox>

        <SelectedPlanSummary maxWidth={isMobile ? '100%' : '360px'} />

        {isMobile && (
          <SelectPlanPurchase
            term={selectedTerm}
            {...selectPlanPurchaseProps}
          />
        )}
      </SelectPlanCheckoutContentFlexWrapper>

      {paymentRequestLoading && (
        <FullScreenSpinner
          backgroundColor="rgba(255,255,255,0.85)"
          direction="column"
          spinnerProps={{ size: 'md' }}
        >
          <Text fontSize="md" fontWeight="medium" mt="17px" textAlign="center">
            Completing your purchase <br />
            and starting your plan.
          </Text>
        </FullScreenSpinner>
      )}
    </>
  );
};

export default PlanSummaryView;
